import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { useReadOnly } from '../../utils/readOnly';

/**
 * Row overflow menu. Five actions inline made every row tall and noisy, so they
 * collapse behind one button. The menu is positioned fixed from the trigger's
 * screen coordinates rather than nested in the cell, because a table cell clips
 * anything that overflows it.
 *
 * Wrap this in `<span onClick={(e) => e.stopPropagation()}>` whenever the table
 * sets `onRowClick` — DataTable puts its handler on the `<tr>`, so without the
 * wrapper, opening the menu also opens the record.
 */
const MENU_WIDTH = 200;

export interface RowActionItem {
  /** Also used as the React key, so labels must be unique within one menu. */
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Red label. For the destructive action, which should confirm before acting. */
  danger?: boolean;
  /** A write item (Edit, Add member) — dropped for a read-only user, like danger. */
  write?: boolean;
  /** Draws a rule above this item, to group the menu. */
  separated?: boolean;
}

export interface RowActionsProps {
  /** Falsy entries are dropped, so callers can write `condition && {...}`. */
  items: Array<RowActionItem | false | null | undefined>;
  isDarkMode?: boolean;
}

const RowActions = ({ items, isDarkMode = false }: RowActionsProps) => {
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const readOnly = useReadOnly();

  // A read-only module keeps the reading items (View, Print, Export) but loses
  // every write: `danger` (Delete / Void) and `write` (Edit, Add member).
  const visible = (items.filter(Boolean) as RowActionItem[])
    .filter((item) => !(readOnly && (item.danger || item.write)));

  useEffect(() => {
    if (!anchor) return undefined;

    // Ignore presses on the menu itself (its own buttons handle those) and on
    // the trigger (whose onClick toggles), so the menu cannot be closed out
    // from under the click that was meant to act on it.
    const onPointerDown = (e) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setAnchor(null);
    };
    const close = () => setAnchor(null);
    const onKey = (e) => { if (e.key === 'Escape') close(); };

    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [anchor]);

  // Every item filtered out leaves nothing to open, so drop the trigger too.
  const empty = visible.length === 0;

  const surface = isDarkMode ? '#2A2A2A' : '#FFFFFF';
  const text = isDarkMode ? '#F5F5F5' : '#333333';
  const border = isDarkMode ? '#444' : '#E0E0E0';

  const toggle = (e) => {
    e.stopPropagation();
    if (anchor) { setAnchor(null); return; }

    const r = e.currentTarget.getBoundingClientRect();
    const estimatedHeight = visible.length * 41 + 12;
    // Clamped to the viewport, and flipped above the trigger when there is no
    // room below, so the menu can never open off-screen on the last row.
    const left = Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    const top = r.bottom + 4 + estimatedHeight > window.innerHeight
      ? Math.max(8, r.top - estimatedHeight - 4)
      : r.bottom + 4;

    setAnchor({ top, left });
  };

  const menu = anchor && (
    <div
      role="menu"
      ref={menuRef}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', top: anchor.top, left: anchor.left, zIndex: 2000,
        width: `${MENU_WIDTH}px`, backgroundColor: surface, color: text,
        border: `1px solid ${border}`, borderRadius: '8px', overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)', textAlign: 'left'
      }}
    >
      {visible.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => { setAnchor(null); item.onClick(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                padding: '10px 14px', background: 'transparent', border: 'none',
                borderTop: item.separated ? `1px solid ${border}` : 'none',
                cursor: item.disabled ? 'not-allowed' : 'pointer',
                opacity: item.disabled ? 0.5 : 1,
                fontSize: '14px', color: item.danger ? '#D32F2F' : text, textAlign: 'left'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDarkMode ? '#3A3A3A' : '#F2F5F9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <span style={{ width: '18px' }}>{item.icon}</span>
              <span>{item.label}</span>
            </button>
      ))}
    </div>
  );

  if (empty) return null;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        aria-label="Row actions"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        title="Actions"
        onClick={toggle}
        style={{
          background: 'transparent', border: `1px solid ${border}`, cursor: 'pointer',
          borderRadius: '6px', padding: '2px 9px', fontSize: '17px', lineHeight: 1.2, color: text
        }}
      >
        ⋯
      </button>

      {/*
        Portalled to <body> so nothing in the table can break it: a cell's
        overflow cannot clip it, an ancestor `transform` cannot re-root its
        fixed positioning, and no local stacking context can bury it.
      */}
      {menu && createPortal(menu, document.body)}
    </>
  );
};

export default RowActions;
